// macadamia-app/src/components/widgets/AuthorAvatar.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';

interface IUser {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    profilePicture: string | null;
}

interface AuthorAvatarProps {
    user: IUser;
    className?: string;
    size?: 'sm' | 'md' | 'lg';
    showTooltip?: boolean;
}

const AuthorAvatar: React.FC<AuthorAvatarProps> = ({
    user,
    className = "",
    size = "sm",
    showTooltip = true
}) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [error, setError] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { getAuthorProfilePicture, user: currentUser } = useAuth();

    // Refs pour éviter les appels multiples
    const lastFetchTime = useRef<number>(0);
    const fetchInProgress = useRef<boolean>(false);

    const tooltipContent = `${user.firstName} ${user.lastName}`;

    // Vérifier si c'est l'utilisateur connecté
    const isCurrentUser = currentUser?.id === user._id;

    const sizes = {
        sm: 'w-6 h-6',
        md: 'w-8 h-8',
        lg: 'w-10 h-10'
    };

    // OPTIMISATION : Mapping taille → preset pour AuthorAvatar
    const getSizePreset = (size: 'sm' | 'md' | 'lg'): string => {
        const presetMap = {
            sm: 'thumbnail',  // 64x64 - parfait pour les petits avatars
            md: 'thumbnail',  // 64x64 - encore suffisant
            lg: 'avatar'      // 96x96 - pour les plus gros
        };
        return presetMap[size];
    };

    const getInitial = (firstName: string) => {
        return firstName.charAt(0).toUpperCase();
    };

    // FIX: Gestion simple des erreurs sans retry automatique
    const handleImageError = useCallback(() => {
        setError(true);
    }, []);

    // FIX: Effet principal sans dépendances instables
    useEffect(() => {
        // Cas 1: Utilisateur connecté - utiliser l'URL du contexte
        if (isCurrentUser && currentUser?.profilePictureUrl) {
            setImageUrl(currentUser.profilePictureUrl);
            setError(false);
            setIsLoading(false);
            return;
        }

        // Cas 2: Autre utilisateur - fetch uniquement si nécessaire
        if (!isCurrentUser && user.profilePicture && !imageUrl && !error) {
            const fetchImage = async () => {
                const now = Date.now();

                // Throttling: éviter les appels trop fréquents
                if (now - lastFetchTime.current < 30000 || fetchInProgress.current) {
                    return;
                }

                fetchInProgress.current = true;
                lastFetchTime.current = now;
                setIsLoading(true);

                try {
                    const preset = getSizePreset(size);
                    const newUrl = await getAuthorProfilePicture(user._id, preset);

                    if (newUrl) {
                        setImageUrl(newUrl);
                        setError(false);
                    } else {
                        setError(true);
                    }
                } catch (err) {
                    console.error("Erreur lors de la récupération de l'image:", err);
                    setError(true);
                } finally {
                    setIsLoading(false);
                    fetchInProgress.current = false;
                }
            };

            fetchImage();
        }

        // CRITIQUE: Deps stables uniquement
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user._id, user.profilePicture, isCurrentUser, currentUser?.profilePictureUrl, size]);

    // Rendu du tooltip
    const Tooltip: React.FC<{
        content: string;
        children: React.ReactNode;
    }> = ({ content, children }) => {
        return (
            <div className="relative group">
                {children}
                <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 
                      bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1
                      bg-mcdm-third-500 text-white text-xs rounded whitespace-nowrap
                      transition-all duration-200 z-10">
                    {content}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 
                       border-4 border-transparent border-t-mcdm-third-500" />
                </div>
            </div>
        );
    };

    // Fonction helper pour envelopper avec Tooltip si nécessaire
    const wrapWithTooltip = (content: React.ReactNode) => {
        return showTooltip ? (
            <Tooltip content={tooltipContent}>{content}</Tooltip>
        ) : <>{content}</>;
    };

    // Afficher l'image si disponible et pas d'erreur
    if (imageUrl && !error && !isLoading) {
        return wrapWithTooltip(
            <img
                src={imageUrl}
                alt={tooltipContent}
                className={`${sizes[size]} rounded-full object-cover object-center ${className}`}
                onError={handleImageError}
            />
        );
    }

    // Afficher un placeholder
    return wrapWithTooltip(
        <div
            className={`${sizes[size]} rounded-full bg-mcdm-third-200 flex items-center justify-center
              text-xs text-white font-medium ${className}`}
        >
            {getInitial(user.firstName)}
        </div>
    );
};

export default AuthorAvatar;